<template>
  <DetailsSection 
    label="Flugauswahl"
  >
    <DropdownField
      field-name="aida-flight"
      :field-value="selectedFlightKey"
      :value-changed="selectFlightConnection"
      :item-list="flightItemList"
      class="mb-3 mt-1"
    />

    <div 
      :class="!selectedFlight ? 'mt-4 mb-2' : ''"
      class="container"
    >
      <div class="row">
        <div class="col">
          <FlightPanel 
            :flight="selectedFlight?.outboundFlight" 
            :message="flightMessage('outbound')"
            direction="O"
          />
        </div>
        <div class="col">
          <FlightPanel
            :flight="selectedFlight?.inboundFlight"
            :message="flightMessage('inbound')"
            direction="I"
          />
        </div>
      </div>
    </div>
  </DetailsSection>

  <DetailsSection 
    label="Bahnfahrt zum/ vom Flughafen"
    type="last"
    class="mt-2"
  >
    <template v-if="hasChildren">
      <FieldLabel>Anzahl Kinder 15 Jahre oder älter</FieldLabel>
      <i
        ref="rail-info-icon"
        class="fa fa-info-circle info-icon color-expinet-blue"
        @mouseover="displayTooltip('rail-info-icon')"
        @mouseleave="hideTooltip('rail-info-icon')"
      />
      <Tooltip 
        :open="tooltips['rail-info-icon'].show" 
        :position="tooltips['rail-info-icon'].pos" 
      >
        <span class="tooltip-text fw-normal">
          Kinder bis einschließlich 14 Jahre reisen kostenfrei mit der Bahn
        </span>
      </Tooltip>
  
      <DropdownField
        field-name="railAdults"
        label=""
        :item-list="getRailAdults"
        :field-value="railAdults.toString()"
        :value-changed="setRailAdultValue"
        class="mb-4"
        :disabled="!hasSelectableRail"
      />
    </template>

    <ToggleGroup
      :options="getRailOptionsRNF()"
      :select="selectRailDirection"
      :selected-key="railDirectionRNF"
      class="d-flex justify-content-stretch mb-2"
    />

    <div class="d-flex flex-column">
      <radio-buttons 
        v-if="isRailValid"
        :items="selectableRailOptionsByDirection" 
        :selected-item="selectedRailOptionId"
        :select="selectRailOption"
        :displayed-label="categoryDescription"
      />
      <div  
        v-if="!!railErrorMessage"
        class="w-100 text-center mt-4 mb-3 fs-16 color-sachlich-grey"
      >
        {{ railErrorMessage }}
      </div>
    </div>
  </DetailsSection>
</template>

<script lang="ts">
import { Direction } from '@/bewotec/openapi'
import FieldLabel from '@/components/common/FieldLabel.vue'
import RadioButtons from '@/components/common/RadioButtons.vue'
import Tooltip from '@/components/common/Tooltip.vue'
import { IItemList } from '@/components/interfaces'
import { useFilterStore, useFlightStore, useUiStore } from '@/store'
import { flightMessage } from '@/store/details/flight/actions'
import { InOutBoundItem } from '@/store/details/flight/interfaces'
import { IFetchRailOption, RailDirection } from '@/store/details/rail/interfaces'
import { IPoint } from '@/store/ui/interfaces'
import { mapActions, mapState, mapWritableState } from 'pinia'
import { defineComponent } from 'vue'
import { children } from '../../../general/master-data'
import { convertToExpitecDirection } from '../../../store/details/rail/helper'
import DropdownField from '../../common/DropdownField.vue'
import ToggleGroup from '../../common/ToggleGroup.vue'
import DetailsSection from '../DetailsSection.vue'
import FlightPanel from './FlightPanel.vue'

export default defineComponent({
  components: {
    DetailsSection,
    DropdownField,
    ToggleGroup,
    FlightPanel,
    RadioButtons,
    FieldLabel,
    Tooltip
  },

  computed: {
    ...mapState(useFilterStore, {
      paxConfig: store => store.paxConfig
    }),
    ...mapState(useFlightStore, {
      flightItems: store => store.flightItems,
      selectedFlightKey: store => store.selectedFlightKey,
      isNoRailAllowed: store => store.isNoRailAllowed,
      railErrorMessage: store => store.getRailErrorMessage
    }),
    ...mapState(useFlightStore, {
      selectedFlight: store => store.selectedFlight,
      railDirectionRNF: store => store.railDirectionRNF,
      selectableRailOptions: store => store.selectableRailOptions,
    }),
    ...mapState(useUiStore, {
      tooltips: store => store.tooltips,
    }),
    ...mapWritableState(useFlightStore, {
      railAdults: 'railAdults',
      selectedRailOptionId: 'selectedRailOptionId',
    }),
    
    hasChildren() {
      return !!this.paxConfig.children
    },

    hasSelectableRail() {
      return !this.railOptionError && !!this.selectableRailOptionsByDirection?.length
    },

    isRailValid() {
      return this.railDirectionRNF !== 'none' && this.hasSelectableRail
    },

    getRailAdults() {
      const railAdults = []

      for (let index = 0; index <= this.paxConfig.children; ++index) {
        railAdults.push(children[index])
      }

      return railAdults
    },
    flightItemList() {
      return this.flightItems as IItemList[]
    },
    railOptionError() {
      return 'error' in this.selectableRailOptions
    },
    selectableRailOptionsByDirection() {
      const selectableRailOptions = this.selectableRailOptions
      const isSelectableRailOptions = !!selectableRailOptions && !('error' in selectableRailOptions)

      return isSelectableRailOptions ? selectableRailOptions[convertToExpitecDirection(this.railDirectionRNF)] : []
    }
  },

  methods: {
    ...mapActions(useFlightStore, {
      fetchRailOptions: 'fetchRailOptions',
      selectRNFRailDirection: 'selectRNFRailDirection',
      setRailOption: 'selectRailOption',
      setInitialRNFDirection: 'setInitialRNFDirection',
      selectDefaultRailOption:'selectDefaultRailOption',
      resetRail: 'resetRail',
      selectFlight: 'selectFlight',
      resetRailWithoutRailAdults: 'resetRailWithoutRailAdults',
      preselectRailOption: 'preselectRailOption',
      flightMessage: 'flightMessage'
    }),
    ...mapActions(useUiStore, {
      hideTooltip: 'hideTooltip',
      showTooltip: 'showTooltip'
    }),

    async selectFlightConnection(_: any, __: any, item: InOutBoundItem) {
      if ('flight' in item) {
        this.selectFlight(item.flight)
        this.resetRailWithoutRailAdults()
        await this.preselectRailOption()
      }
    },
    getRailOptionsRNF() {
      const selectedFlightDirection = this.selectedFlight?.direction
      const isBothWays = selectedFlightDirection === Direction.RT || selectedFlightDirection === Direction.OJ
      const selectableRailOptions = this.selectableRailOptions
      const isSelectableRailOptions = !!selectableRailOptions && !('error' in selectableRailOptions)

      return [
        {
          key: 'none',
          label: 'keine Bahnfahrt',
          available: this.isNoRailAllowed,
        },
        {
          key: 'both',
          label: 'Hin- und Rückfahrt',
          available: 
            isSelectableRailOptions && 
            isBothWays && 
            !!selectableRailOptions[Direction.RT]?.length,
        },
        {
          key: 'outbound',
          label: 'Hinfahrt',
          available: 
            isSelectableRailOptions && 
            (isBothWays || selectedFlightDirection === Direction.O) && 
            !!selectableRailOptions[Direction.O]?.length,
        },
        {
          key: 'inbound',
          label: 'Rückfahrt',
          available: 
            isSelectableRailOptions && 
            (isBothWays || selectedFlightDirection === Direction.R) && 
            !!selectableRailOptions[Direction.R]?.length,
        },
      ]
    },
    async setRailAdultValue(_: any, fieldValue: string) {
      this.resetRail()
      this.railAdults = parseInt(fieldValue, 10)
      await this.fetchRailOptions()
    },
    selectRailDirection(option: RailDirection) {
      this.selectRNFRailDirection(option)
      this.selectRailOption(this.selectedRailOptionId)
    },
    selectRailOption(optionId: string) {
      this.selectedRailOptionId = optionId
      this.setRailOption(optionId)
    },

    categoryDescription: (option: IFetchRailOption) => {
      return !!option && 'railClassName' in option ? option.railClassName : ''
    },

    displayTooltip(name: string) {
      const refname = this.$refs[name] as HTMLElement

      if (!!refname) {
        const rect = refname.getBoundingClientRect()
        const pos: IPoint = { x: rect.left, y: rect.top }
        this.showTooltip({ name, pos })
      }
    },
  },
})
</script>
